import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../../lib/firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import SEO from "../../components/SEO";
import { isWhiteLabelEnabled } from "../../utils/whiteLabelUtils";
import { 
  PlayIcon,
  ClockIcon,
  BookOpenIcon,
  CheckCircleIcon,
  UserIcon,
  StarIcon
} from "../../components/icons/OutlineIcons";
import { GraduationCap, LogOut, User as UserIconLucide, BookOpen, Mail, BarChart3, Menu, X } from "lucide-react";
import AuthModal from "../../components/AuthModal";

export default function CoursePublic() {
  const { id } = useParams();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [lessons, setLessons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [instructor, setInstructor] = useState<any>(null);
  const [courseSEO, setCourseSEO] = useState<any>(null);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const themeColor = instructor?.themeColor || instructor?.buttonColor || "#8B5CF6";
  const headingColor = instructor?.headingColor || "#1F2937";
  const buttonColor = instructor?.buttonColor || instructor?.themeColor || "#8B5CF6";

  console.log('CoursePublic - Instructor:', instructor);
  console.log('CoursePublic - Colors:', { themeColor, headingColor, buttonColor });

  useEffect(() => {
    async function load() {
      if (!id) return;

      // Load course
      const snap = await getDoc(doc(db, "courses", id));
      if (snap.exists()) {
        const data = snap.data();
        setCourse(data);

        // Load instructor
        const instSnap = await getDoc(doc(db, "users", data.instructorId));
        if (instSnap.exists()) {
          const instructorData = instSnap.data();
          setInstructor(instructorData);
          // Update document title with instructor's brand name (only for Pro Yearly and Pro+ Yearly)
          const tier = instructorData.subscriptionTier || '';
          const isYearlyPlan = tier === 'pro_yearly' || tier === 'pro_plus_yearly';
          if (isYearlyPlan) {
            const instructorName = instructorData.displayName || instructorData.businessName || 'EduMi LMS';
            document.title = instructorName;
          } else {
            document.title = 'EduMi LMS';
          }
        }

        // Check if white-label is enabled (Pro+ Yearly only)
        const whiteLabelStatus = await isWhiteLabelEnabled(data.instructorId);
        setIsWhiteLabel(whiteLabelStatus);

        // Load SEO data for this course
        const seoSnap = await getDoc(doc(db, "courseSEO", id));
        if (seoSnap.exists()) {
          setCourseSEO(seoSnap.data());
        }
      }

      // Load lessons
      const q = query(
        collection(db, "lessons"), 
        where("courseId", "==", id)
      );
      const ls = await getDocs(q);
      const lessonsData = ls.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by orderIndex in JavaScript
      lessonsData.sort((a: any, b: any) => (a.orderIndex || 0) - (b.orderIndex || 0));
      setLessons(lessonsData);

      setLoading(false);
    }
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดคอร์ส...</p>
        </div>
      </div>
    );
  }
  
  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-textDark mb-2">ไม่พบคอร์สนี้</h2>
          <p className="text-textLight mb-6">ลิงก์อาจไม่ถูกต้องหรือคอร์สถูกลบไปแล้ว</p>
          <Link to="/" className="inline-block bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold">
            กลับหน้าหลัก
          </Link>
        </div>
      </div>
    );
  }

  const totalDuration = lessons.length * 15;

  // SEO data
  const seoTitle = courseSEO?.enabled && courseSEO?.title 
    ? courseSEO.title 
    : course?.title || "คอร์สเรียน";
  const seoDescription = courseSEO?.enabled && courseSEO?.description 
    ? courseSEO.description 
    : course?.description || "เรียนรู้กับคอร์สคุณภาพจาก EduMi";
  const seoKeywords = courseSEO?.enabled && courseSEO?.keywords 
    ? courseSEO.keywords 
    : "";
  const seoImage = courseSEO?.ogImage || course?.thumbnailUrl || "https://edumi.app/logo.png";

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        ogImage={seoImage}
        canonicalUrl={`https://edumi.app/course/${id}`}
      />
      <div className="min-h-screen bg-gray-50">
      {/* Navigation Menu */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              {instructor?.profileImage ? (
                <img src={instructor.profileImage} alt={instructor.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-lg" />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg" style={{ background: themeColor }}>
                  {instructor?.name?.[0]?.toUpperCase() || "T"}
                </div>
              )}
              <div>
                <h2 className="font-bold text-gray-800 font-sans">{instructor?.name || "ผู้สอน"}</h2>
                <p className="text-xs text-gray-500 font-sans flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  ผู้สอนมืออาชีพ
                </p>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => {
                  const isSubdomain = window.location.hostname !== 'localhost' && 
                                     window.location.hostname !== '127.0.0.1' && 
                                     !window.location.hostname.startsWith('edumi.co') &&
                                     window.location.hostname.includes('.edumi.co');
                  
                  if (isSubdomain) {
                    navigate('/');
                  } else {
                    const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                    navigate(baseUrl);
                  }
                }}
                className="px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                <BookOpen className="w-4 h-4" />
                คอร์สทั้งหมด
              </button>
              {user && (
                <>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=my');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=my`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800"
                  >
                    <UserIconLucide className="w-4 h-4" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=history');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=history`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800"
                  >
                    <Mail className="w-4 h-4" />
                    ประวัติการชำระเงิน
                  </button>
                </>
              )}
              {user ? (
                <button
                  onClick={() => auth.signOut()}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 rounded-xl font-semibold font-sans text-red-600 transition-all flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  ออกจากระบบ
                </button>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-2 text-white rounded-xl font-semibold font-sans shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
                >
                  <UserIconLucide className="w-4 h-4" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {showMobileMenu && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4 space-y-2">
              <button
                onClick={() => {
                  const isSubdomain = window.location.hostname !== 'localhost' && 
                                     window.location.hostname !== '127.0.0.1' && 
                                     !window.location.hostname.startsWith('edumi.co') &&
                                     window.location.hostname.includes('.edumi.co');
                  
                  if (isSubdomain) {
                    navigate('/');
                  } else {
                    const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                    navigate(baseUrl);
                  }
                  setShowMobileMenu(false);
                }}
                className="w-full px-4 py-3 rounded-xl font-semibold font-sans transition-all flex items-center gap-3 text-gray-600 hover:bg-gray-50"
              >
                <BookOpen className="w-5 h-5" />
                คอร์สทั้งหมด
              </button>
              {user && (
                <>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=my');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=my`);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 rounded-xl font-semibold font-sans transition-all flex items-center gap-3 text-gray-600 hover:bg-gray-50"
                  >
                    <UserIconLucide className="w-5 h-5" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=history');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=history`);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 rounded-xl font-semibold font-sans transition-all flex items-center gap-3 text-gray-600 hover:bg-gray-50"
                  >
                    <Mail className="w-5 h-5" />
                    ประวัติการชำระเงิน
                  </button>
                </>
              )}
              {user ? (
                <button
                  onClick={() => {
                    auth.signOut();
                    setShowMobileMenu(false);
                  }}
                  className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 rounded-xl font-semibold font-sans text-red-600 transition-all flex items-center gap-3"
                >
                  <LogOut className="w-5 h-5" />
                  ออกจากระบบ
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setShowMobileMenu(false);
                  }}
                  className="w-full px-4 py-3 text-white rounded-xl font-semibold font-sans shadow-lg flex items-center gap-3"
                  style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
                >
                  <UserIconLucide className="w-5 h-5" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[350px] overflow-hidden">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="w-full h-full object-contain bg-gray-900"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Course Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="inline-block text-white px-4 py-1 rounded-full text-sm font-semibold mb-4" style={{ background: themeColor }}>
              {course.category || "ทั่วไป"}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {course.title}
            </h1>
            <div className="flex items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                <span>{instructor?.name || "Instructor"}</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpenIcon className="w-5 h-5" />
                <span>{lessons.length} บทเรียน</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5" />
                <span>~{totalDuration} นาที</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Course Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* What You'll Learn */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3" style={{ color: headingColor }}>
                <div className="bg-green-100 p-2 rounded-xl">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                สิ่งที่จะได้เรียนรู้
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-textDark">ความเข้าใจพื้นฐานของเนื้อหาในคอร์ส</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-textDark">เรียนรู้แบบ Step-by-Step บนมือถือ</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-textDark">เข้าถึงบทเรียนได้ทุกที่ทุกเวลา</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-textDark">วิดีโอคุณภาพสูงจากผู้เชี่ยวชาญ</span>
                </div>
              </div>
            </div>

            {/* Course Content */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3" style={{ color: headingColor }}>
                <div className="bg-blue-100 p-2 rounded-xl">
                  <BookOpenIcon className="w-6 h-6 text-blue-600" />
                </div>
                เนื้อหาในคอร์ส
              </h2>
              
              <div className="space-y-3">
                {lessons.map((lesson, index) => (
                  <div
                    key={lesson.id}
                    className="group bg-gray-50 rounded-2xl p-4 transition-all duration-300 border-2 border-transparent hover:shadow-md"
                  >
                    <div className="flex items-start gap-4">
                      {/* Lesson Number */}
                      <div className="flex-shrink-0">
                        <div className="text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold shadow-md" style={{ background: themeColor }}>
                          {index + 1}
                        </div>
                      </div>
                      
                      {/* Lesson Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-textDark mb-1 transition-colors">
                          {lesson.title}
                        </h3>
                        {lesson.description && (
                          <p className="text-sm text-textLight line-clamp-2">
                            {lesson.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Play Icon */}
                      <div className="flex-shrink-0">
                        <div className="bg-white p-2 rounded-xl transition-colors">
                          <PlayIcon className="w-5 h-5" style={{ color: themeColor }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructor */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: headingColor }}>เกี่ยวกับผู้สอน</h2>
              
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  {instructor?.profileImage ? (
                    <img 
                      src={instructor.profileImage} 
                      alt={instructor.name}
                      className="w-20 h-20 rounded-2xl object-cover shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg" style={{ background: themeColor }}>
                      {instructor?.name?.[0]?.toUpperCase() || "I"}
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-textDark mb-2">
                    {instructor?.name || "Instructor"}
                  </h3>
                  {instructor?.bio ? (
                    <p className="text-textLight mb-4 whitespace-pre-line">
                      {instructor.bio}
                    </p>
                  ) : !isWhiteLabel && (
                    <p className="text-textLight mb-4">
                      ผู้สอนคอร์สออนไลน์บน EduMi
                    </p>
                  )}
                  
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <BookOpenIcon className="w-4 h-4" style={{ color: themeColor }} />
                      <span className="text-textLight">1 คอร์ส</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-textLight">5.0 คะแนน</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Purchase Card (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-gray-100">
                {/* Price */}
                <div className="text-center mb-6">
                  <p className="text-sm text-textLight mb-3">ราคาคอร์ส</p>
                  <div className="bg-white rounded-2xl px-6 py-4 border-2 border-gray-200">
                    <p className="text-4xl font-bold" style={{ 
                      color: buttonColor
                    }}>
                      ฿{course.price?.toLocaleString()}
                    </p>
                  </div>
                  <p className="text-sm text-textLight mt-3">ซื้อครั้งเดียว เรียนได้ตลอดชีพ</p>
                </div>

                {/* CTA Button */}
                {user && (course?.instructorId === user.uid || user.role === 'admin') ? (
                  // Instructor viewing their own course OR Admin - show preview button
                  <Link to={`/courses/${id}/learn/1${course?.instructorId ? `?instructorId=${course.instructorId}` : ''}`}>
                    <button 
                      className="relative w-full text-white py-5 rounded-xl font-bold text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 mb-6 overflow-hidden group"
                      style={{ 
                        background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)`
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                      <span className="relative">
                        {user.role === 'admin' ? 'เข้าชมคอร์ส (Admin)' : 'เข้าชมคอร์ส (ผู้สอน)'}
                      </span>
                    </button>
                  </Link>
                ) : (
                  // Regular user - show purchase button
                  <Link to={`/checkout/${id}`}>
                    <button 
                      className="relative w-full text-white py-5 rounded-xl font-bold text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 mb-6 overflow-hidden group"
                      style={{ 
                        background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)`
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                      <span className="relative">ซื้อคอร์สนี้</span>
                    </button>
                  </Link>
                )}

                {/* Features */}
                <div className="space-y-3 pt-6 border-t-2 border-gray-100">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-textDark">เข้าถึงได้ตลอดชีพ</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-textDark">เรียนบนมือถือได้</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-textDark">วิดีโอ HD คุณภาพสูง</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-textDark">ใบประกาศนียบัตร</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t-2 border-gray-100">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: themeColor }}>{lessons.length}</p>
                    <p className="text-xs text-textLight">บทเรียน</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: themeColor }}>{totalDuration}</p>
                    <p className="text-xs text-textLight">นาที</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: themeColor }}>5.0</p>
                    <p className="text-xs text-textLight">คะแนน</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        themeColor={themeColor}
      />
      </div>
    </>
  );
}
